import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, CheckCircle, ChevronLeft, ChevronRight,
  Loader2, PlayCircle, FileText, BarChart3, Video, File, Menu, X,
  Clock, AlertCircle, Trophy, RotateCcw, Check, Circle
} from 'lucide-react';

// Helper to extract video embed URL
function getVideoEmbed(url) {
  if (!url) return '';
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url;
}

export default function LessonViewer() {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Quiz state
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [quizTimeLeft, setQuizTimeLeft] = useState(null);

  useEffect(() => { fetchData(); }, [courseId, lessonId]);

  // Quiz timer
  useEffect(() => {
    if (quizTimeLeft === null || quizTimeLeft <= 0 || quizResult) return;
    const timer = setInterval(() => {
      setQuizTimeLeft(t => {
        if (t <= 1) {
          handleQuizSubmit();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [quizTimeLeft, quizResult]);

  const fetchData = async () => {
    setLoading(true);
    setQuizResult(null);
    setQuizAnswers({});
    try {
      const [courseRes, progressRes] = await Promise.all([
        api.get(`/courses/${courseId}`),
        api.get(`/courses/${courseId}/progress`)
      ]);
      setCourse(courseRes.data);
      setProgress(progressRes.data);

      // Find current lesson
      let foundLesson = null;
      for (const mod of courseRes.data.modules || []) {
        for (const les of mod.lessons || []) {
          if (les.lesson_id === lessonId) {
            foundLesson = { ...les, module_title: mod.title, module_id: mod.module_id };
            break;
          }
        }
        if (foundLesson) break;
      }
      if (!foundLesson) {
        toast.error('Lesson not found');
        navigate(`/courses/${courseId}`);
        return;
      }
      setLesson(foundLesson);

      // Initialize quiz timer if needed
      if (foundLesson.type === 'quiz' && foundLesson.quiz_data?.time_limit_minutes) {
        setQuizTimeLeft(foundLesson.quiz_data.time_limit_minutes * 60);
      }
    } catch (err) {
      toast.error('Failed to load lesson');
      navigate(`/courses/${courseId}`);
    } finally {
      setLoading(false);
    }
  };

  const markComplete = async () => {
    setCompleting(true);
    try {
      const res = await api.post(`/lessons/${lessonId}/complete`);
      setProgress(prev => ({
        ...prev,
        completed_lessons: res.data.completed_lessons,
        progress_percent: res.data.progress_percent,
        completed: res.data.completed
      }));

      if (res.data.completed) {
        if (res.data.certificate_issued) {
          toast.success(
            <div className="flex flex-col gap-1">
              <p className="font-bold">Course Completed! 🎉</p>
              <p className="text-sm">Your certificate has been issued. Check your dashboard.</p>
            </div>,
            { duration: 5000 }
          );
        } else {
          toast.success('Course completed! 🎉');
        }
      } else {
        toast.success('Lesson completed!');
      }

      const next = getAdjacentLesson('next');
      if (next && !res.data.completed) {
        setTimeout(() => navigate(`/courses/${courseId}/lessons/${next.lesson_id}`), 500);
      }
    } catch (err) {
      toast.error('Failed to mark lesson as complete');
    } finally {
      setCompleting(false);
    }
  };

  const handleQuizSubmit = async () => {
    if (quizSubmitting || quizResult) return;
    setQuizSubmitting(true);
    try {
      const res = await api.post(`/lessons/${lessonId}/quiz-submit`, { answers: quizAnswers });
      setQuizResult(res.data);

      if (res.data.passed) {
        // Update UI progress (optional but nice)
        setProgress(prev => ({
          ...prev,
          completed_lessons: Array.from(new Set([...(prev?.completed_lessons || []), lessonId])),
          progress_percent: res.data.progress_percent || prev?.progress_percent,
          completed: res.data.completed || prev?.completed
        }));

        if (res.data.completed) {
          if (res.data.certificate_issued) {
            toast.success(
              <div className="flex flex-col gap-1">
                <p className="font-bold">Quiz Passed & Course Completed! 🎉</p>
                <p className="text-sm">Your certificate has been issued. Check your dashboard.</p>
              </div>,
              { duration: 5000 }
            );
          } else {
            toast.success('Quiz passed and course completed! 🎉');
          }
        } else {
          toast.success('Quiz passed! Great job!');
        }
      } else {
        toast.error(`You scored ${res.data.score_percent}%. Required: ${res.data.passing_score}%`);
      }
    } catch (err) {
      toast.error('Failed to submit quiz');
    } finally {
      setQuizSubmitting(false);
    }
  };

  const handleQuizAnswerChange = (questionId, optionIndex, allowMultiple) => {
    setQuizAnswers(prev => {
      if (allowMultiple) {
        const current = prev[questionId] || [];
        if (current.includes(optionIndex)) {
          return { ...prev, [questionId]: current.filter(i => i !== optionIndex) };
        }
        return { ...prev, [questionId]: [...current, optionIndex] };
      }
      return { ...prev, [questionId]: [optionIndex] };
    });
  };

  const resetQuiz = () => {
    setQuizResult(null);
    setQuizAnswers({});
    if (lesson?.quiz_data?.time_limit_minutes) {
      setQuizTimeLeft(lesson.quiz_data.time_limit_minutes * 60);
    }
  };

  const getAdjacentLesson = (direction) => {
    if (!course?.modules) return null;
    const allLessons = [];
    course.modules.forEach(m => {
      m.lessons?.forEach(l => allLessons.push({ ...l, module_title: m.title }));
    });
    const currentIdx = allLessons.findIndex(l => l.lesson_id === lessonId);
    if (direction === 'prev' && currentIdx > 0) return allLessons[currentIdx - 1];
    if (direction === 'next' && currentIdx < allLessons.length - 1) return allLessons[currentIdx + 1];
    return null;
  };

  const isLessonCompleted = (lid) => progress?.completed_lessons?.includes(lid) || false;

  const getLessonIcon = (type, size = 4) => {
    const cls = `h-${size} w-${size}`;
    switch (type) {
      case 'video': return <Video className={cls} />;
      case 'pdf': return <File className={cls} />;
      case 'quiz': return <BarChart3 className={cls} />;
      default: return <FileText className={cls} />;
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="h-8 w-8 text-white animate-spin" />
      </div>
    );
  }

  if (!lesson || !course) return null;

  const prevLesson = getAdjacentLesson('prev');
  const nextLesson = getAdjacentLesson('next');
  const currentCompleted = isLessonCompleted(lessonId);

  return (
    <div className="min-h-screen bg-slate-900 flex" data-testid="lesson-viewer">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-40 w-80 bg-slate-800 transform transition-transform duration-300 lg:relative lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center justify-between mb-3">
              <Button variant="ghost" size="sm" onClick={() => navigate(`/courses/${courseId}`)} className="text-slate-400 hover:text-white p-1" data-testid="back-to-course-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-400 hover:text-white">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <h3 className="text-white font-semibold text-sm line-clamp-2">{course.title}</h3>
            <div className="mt-3">
              <div className="flex justify-between text-xs text-slate-400 mb-1">
                <span>Progress</span>
                <span>{progress?.progress_percent || 0}%</span>
              </div>
              <Progress value={progress?.progress_percent || 0} className="h-1.5 bg-slate-700" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {course.modules?.map((mod) => (
              <div key={mod.module_id} className="mb-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{mod.title}</p>
                <div className="space-y-1">
                  {mod.lessons?.map((les) => (
                    <button
                      key={les.lesson_id}
                      onClick={() => {
                        navigate(`/courses/${courseId}/lessons/${les.lesson_id}`);
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${les.lesson_id === lessonId ? 'bg-[#1a5c5c] text-white' : 'text-slate-300 hover:bg-slate-700'}`}
                    >
                      {isLessonCompleted(les.lesson_id) ? (
                        <CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                      ) : (
                        <span className="flex-shrink-0 text-slate-500">{getLessonIcon(les.type)}</span>
                      )}
                      <span className="line-clamp-1">{les.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <Button variant="ghost" onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-400 hover:text-white">
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <span className="hidden md:inline">{lesson.module_title}</span>
            <ChevronRight className="h-4 w-4 hidden md:block" />
            <span className="text-white font-medium">{lesson.title}</span>
          </div>
          <div className="flex items-center gap-2">
            {prevLesson && (
              <Button variant="ghost" size="sm" onClick={() => navigate(`/courses/${courseId}/lessons/${prevLesson.lesson_id}`)} className="text-slate-400 hover:text-white">
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            {nextLesson && (
              <Button variant="ghost" size="sm" onClick={() => navigate(`/courses/${courseId}/lessons/${nextLesson.lesson_id}`)} className="text-slate-400 hover:text-white">
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Lesson Type Indicator */}
            <div className="flex items-center gap-2 text-slate-400 text-sm mb-4">
              {getLessonIcon(lesson.type)}
              <span className="capitalize">{lesson.type}</span>
              <span>•</span>
              <span>{lesson.type === 'quiz' ? `${lesson.quiz_data?.questions?.length || 0} questions` : `${lesson.duration_minutes} min`}</span>
            </div>

            {/* Video Content */}
            {lesson.type === 'video' && lesson.video_url && (
              <div className="aspect-video bg-black rounded-lg mb-6 overflow-hidden">
                <iframe
                  src={getVideoEmbed(lesson.video_url)}
                  className="w-full h-full"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  title={lesson.title}
                />
              </div>
            )}

            {lesson.type === 'video' && !lesson.video_url && (
              <div className="aspect-video bg-slate-800 rounded-lg mb-6 flex items-center justify-center">
                <div className="text-center">
                  <PlayCircle className="h-16 w-16 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500">Video content coming soon</p>
                </div>
              </div>
            )}

            {/* Text/Rich Content */}
            {lesson.type === 'text' && (
              <div className="prose prose-invert prose-slate max-w-none">
                <h1 className="text-2xl font-bold text-white mb-6">{lesson.title}</h1>
                {lesson.content ? (
                  <div
                    className="text-slate-300 leading-relaxed lesson-content"
                    dangerouslySetInnerHTML={{ __html: lesson.content }}
                  />
                ) : (
                  <div className="bg-slate-800 rounded-lg p-8 text-center">
                    <FileText className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-500">Lesson content is being prepared.</p>
                  </div>
                )}
              </div>
            )}

            {/* Quiz Content */}
            {lesson.type === 'quiz' && lesson.quiz_data && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h1 className="text-2xl font-bold text-white">{lesson.title}</h1>
                  {quizTimeLeft !== null && !quizResult && (
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${quizTimeLeft < 60 ? 'bg-red-500/20 text-red-400' : 'bg-slate-700 text-slate-300'}`}>
                      <Clock className="h-4 w-4" />
                      <span className="font-mono text-sm">{formatTime(quizTimeLeft)}</span>
                    </div>
                  )}
                </div>

                {/* Quiz Info */}
                <div className="bg-slate-800 rounded-lg p-4 flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2 text-slate-400">
                    <BarChart3 className="h-4 w-4" />
                    <span>{lesson.quiz_data.questions?.length || 0} questions</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <Trophy className="h-4 w-4" />
                    <span>Pass: {lesson.quiz_data.passing_score}%</span>
                  </div>
                  {lesson.quiz_data.time_limit_minutes && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Clock className="h-4 w-4" />
                      <span>{lesson.quiz_data.time_limit_minutes} min limit</span>
                    </div>
                  )}
                </div>

                {/* Quiz Result */}
                {quizResult && (
                  <div className={`rounded-xl p-6 ${quizResult.passed ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {quizResult.passed ? (
                          <Trophy className="h-8 w-8 text-emerald-400" />
                        ) : (
                          <AlertCircle className="h-8 w-8 text-red-400" />
                        )}
                        <div>
                          <h3 className={`text-lg font-bold ${quizResult.passed ? 'text-emerald-400' : 'text-red-400'}`}>
                            {quizResult.passed ? 'Quiz Passed!' : 'Quiz Not Passed'}
                          </h3>
                          <p className="text-slate-400 text-sm">
                            Score: {quizResult.score_percent}% ({quizResult.earned_points}/{quizResult.total_points} points)
                          </p>
                        </div>
                      </div>
                      {!quizResult.passed && (
                        <Button onClick={resetQuiz} variant="outline" className="border-slate-600 text-slate-300">
                          <RotateCcw className="h-4 w-4 mr-2" /> Try Again
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Questions */}
                <div className="space-y-6">
                  {lesson.quiz_data.questions?.map((question, qIndex) => {
                    const qId = question.question_id || qIndex.toString();
                    const selectedAnswers = quizAnswers[qId] || [];
                    const resultForQ = quizResult?.results?.find(r => r.question_id === qId);

                    return (
                      <div
                        key={qId}
                        className={`bg-slate-800 rounded-xl p-6 ${resultForQ ? (resultForQ.correct ? 'ring-2 ring-emerald-500/50' : 'ring-2 ring-red-500/50') : ''}`}
                        data-testid={`quiz-question-${qIndex}`}
                      >
                        <div className="flex items-start gap-3 mb-4">
                          <Badge variant="outline" className="shrink-0 text-slate-400 border-slate-600">
                            Q{qIndex + 1}
                          </Badge>
                          <div className="flex-1">
                            <p className="text-white font-medium">{question.question_text}</p>
                            {question.allow_multiple && (
                              <p className="text-xs text-slate-500 mt-1">Select all that apply</p>
                            )}
                          </div>
                          {resultForQ && (
                            <div className={`h-6 w-6 rounded-full flex items-center justify-center ${resultForQ.correct ? 'bg-emerald-500' : 'bg-red-500'}`}>
                              {resultForQ.correct ? <Check className="h-4 w-4 text-white" /> : <X className="h-4 w-4 text-white" />}
                            </div>
                          )}
                        </div>

                        <div className="space-y-2 ml-8">
                          {question.options.map((option, oIndex) => {
                            const isSelected = selectedAnswers.includes(oIndex);
                            const isCorrect = resultForQ?.correct_answer?.includes(oIndex);
                            const wasUserAnswer = resultForQ?.user_answer?.includes(oIndex);

                            let optionClass = 'border-slate-600 hover:border-slate-500';
                            if (quizResult) {
                              if (isCorrect) optionClass = 'border-emerald-500 bg-emerald-500/10';
                              else if (wasUserAnswer && !isCorrect) optionClass = 'border-red-500 bg-red-500/10';
                            } else if (isSelected) {
                              optionClass = 'border-[#1a5c5c] bg-[#1a5c5c]/10';
                            }

                            return (
                              <button
                                key={oIndex}
                                onClick={() => !quizResult && handleQuizAnswerChange(qId, oIndex, question.allow_multiple)}
                                disabled={!!quizResult}
                                className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${optionClass}`}
                              >
                                <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${isSelected && !quizResult ? 'border-[#1a5c5c] bg-[#1a5c5c]' :
                                    isCorrect ? 'border-emerald-500 bg-emerald-500' :
                                      wasUserAnswer ? 'border-red-500 bg-red-500' : 'border-slate-500'
                                  }`}>
                                  {(isSelected || isCorrect || wasUserAnswer) && <Check className="h-3 w-3 text-white" />}
                                </div>
                                <span className={`text-sm ${isCorrect ? 'text-emerald-400' : wasUserAnswer && !isCorrect ? 'text-red-400' : 'text-slate-300'}`}>
                                  {option.text}
                                </span>
                              </button>
                            );
                          })}
                        </div>

                        {/* Explanation */}
                        {resultForQ && question.explanation && (
                          <div className="mt-4 ml-8 p-3 bg-slate-700/50 rounded-lg">
                            <p className="text-xs text-slate-500 mb-1">Explanation:</p>
                            <p className="text-sm text-slate-300">{question.explanation}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Submit Button */}
                {!quizResult && lesson.quiz_data.questions?.length > 0 && (
                  <div className="flex justify-center pt-4">
                    <Button
                      onClick={handleQuizSubmit}
                      disabled={quizSubmitting || Object.keys(quizAnswers).length === 0}
                      className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white px-8"
                      data-testid="submit-quiz-btn"
                    >
                      {quizSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Submit Quiz
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Fallback for quiz without data */}
            {lesson.type === 'quiz' && !lesson.quiz_data && (
              <div className="bg-slate-800 rounded-lg p-8 text-center">
                <BarChart3 className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500">Quiz content is being prepared.</p>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Navigation */}
        <div className="border-t border-slate-800 p-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div>
              {prevLesson && (
                <Button variant="ghost" onClick={() => navigate(`/courses/${courseId}/lessons/${prevLesson.lesson_id}`)} className="text-slate-400 hover:text-white" data-testid="prev-lesson-btn">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Previous
                </Button>
              )}
            </div>

            {lesson.type !== 'quiz' && (
              <Button
                onClick={markComplete}
                disabled={completing || currentCompleted}
                className={currentCompleted ? 'bg-emerald-600 hover:bg-emerald-600 text-white cursor-default' : 'bg-[#1a5c5c] hover:bg-[#154a4a] text-white'}
                data-testid="mark-complete-btn"
              >
                {completing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : currentCompleted ? <CheckCircle className="h-4 w-4 mr-2" /> : null}
                {currentCompleted ? 'Completed' : 'Mark as Complete'}
              </Button>
            )}

            {lesson.type === 'quiz' && quizResult?.passed && (
              <Button className="bg-emerald-600 text-white cursor-default" disabled>
                <CheckCircle className="h-4 w-4 mr-2" /> Quiz Passed
              </Button>
            )}

            <div>
              {nextLesson && (
                <Button variant="ghost" onClick={() => navigate(`/courses/${courseId}/lessons/${nextLesson.lesson_id}`)} className="text-slate-400 hover:text-white" data-testid="next-lesson-btn">
                  Next <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lesson content styles */}
      <style>{`
        .lesson-content h1 { font-size: 1.5rem; font-weight: 700; margin-bottom: 1rem; color: white; }
        .lesson-content h2 { font-size: 1.25rem; font-weight: 600; margin: 1.5rem 0 0.75rem; color: white; }
        .lesson-content h3 { font-size: 1.1rem; font-weight: 600; margin: 1.25rem 0 0.5rem; color: white; }
        .lesson-content p { margin-bottom: 1rem; line-height: 1.7; }
        .lesson-content ul, .lesson-content ol { margin-bottom: 1rem; padding-left: 1.5rem; }
        .lesson-content li { margin-bottom: 0.5rem; }
        .lesson-content a { color: #4fd1c5; text-decoration: underline; }
        .lesson-content blockquote { border-left: 3px solid #475569; padding-left: 1rem; margin: 1rem 0; color: #94a3b8; }
        .lesson-content code { background: #334155; padding: 0.2rem 0.4rem; border-radius: 4px; font-size: 0.9em; }
        .lesson-content pre { background: #1e293b; padding: 1rem; border-radius: 8px; overflow-x: auto; margin: 1rem 0; }
        .lesson-content img { max-width: 100%; border-radius: 8px; margin: 1rem 0; }
      `}</style>
    </div>
  );
}
